import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Pencil, IdCard as IdIcon, Search, BadgeDollarSign, Ban, Trash2, UserPlus } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { usePlans } from "../../lib/plans";
import { AddMemberDialog } from "./AddMemberDialog";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useI18n } from "../../i18n";
import { api, formatError, fmtETB } from "../../lib/api";
import { EditMemberDialog } from "./EditMemberDialog";
import { IdCardDialog } from "./IdCardDialog";

const STATUS_STYLE = { pending: "border-amber-500/50 text-amber-500", approved: "border-emerald-500/50 text-emerald-500", rejected: "border-red-500/50 text-red-500", blocked: "border-zinc-500/60 bg-zinc-500/10 text-zinc-400" };
const PAY_STYLE = { unpaid: "text-muted-foreground", submitted: "border-amber-500/50 text-amber-500", paid: "border-emerald-500/50 text-emerald-500", rejected: "border-red-500/50 text-red-500" };

const IconBtn = ({ onClick, title, children, testId, disabled }) => (
  <button onClick={onClick} title={title} disabled={disabled} className="flex h-8 w-8 items-center justify-center rounded-full border transition-colors hover:bg-accent disabled:opacity-30" data-testid={testId}>{children}</button>
);

export const MembersTab = ({ onChange }) => {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [card, setCard] = useState(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const { planName } = usePlans();

  const load = useCallback(() => api.get("/admin/users", { params: { status, q: q || undefined } }).then((r) => setUsers(r.data)), [status, q]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  const update = async (u, body) => {
    try { await api.put(`/admin/users/${u.id}`, body); toast.success(t("adm_saved")); load(); onChange(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/users/${deleting.id}`); toast.success(t("adm_saved")); setDeleting(null); load(); onChange(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="lux-card p-5">
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input className="h-10 pl-9" placeholder={t("adm_search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" /></div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-10 sm:w-48" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "pending", "approved", "rejected", "blocked"].map((s) => <SelectItem key={s} value={s} data-testid={`admin-filter-${s}`}>{t(`adm_${s}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={() => setAdding(true)} className="btn-gold !py-2" data-testid="admin-add-member-btn"><UserPlus className="h-4 w-4" />{t("adm_add_member")}</button>
      </div>
      <div className="mt-5 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t("adm_name")}</TableHead><TableHead>{t("adm_phone")}</TableHead><TableHead>{t("adm_package")}</TableHead>
            <TableHead>{t("adm_status")}</TableHead><TableHead>{t("adm_payment")}</TableHead><TableHead>{t("adm_support")}</TableHead><TableHead className="text-right">{t("adm_actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground" data-testid="admin-no-users">{t("adm_no_users")}</TableCell></TableRow>}
            {users.map((u) => {
              const ready = u.status === "approved" && u.payment_status === "paid";
              return (
                <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <TableCell><p className="font-medium">{u.full_name}</p>{u.member_id && <p className="font-mono text-xs text-muted-foreground">{u.member_id}</p>}</TableCell>
                  <TableCell className="font-mono text-xs">{u.phone}</TableCell>
                  <TableCell>{planName(u.package)}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS_STYLE[u.status]} data-testid={`admin-user-status-${u.id}`}>{t(`st_${u.status}`)}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className={PAY_STYLE[u.payment_status]} data-testid={`admin-user-payment-${u.id}`}>{t(`pay_${u.payment_status}`)}</Badge>
                    {(u.payment_method || u.payment_reference) && <p className="mt-1 font-mono text-[10px] text-muted-foreground">{u.payment_method}{u.payment_reference && ` · ${u.payment_reference}`}</p>}</TableCell>
                  <TableCell className="font-mono">{fmtETB(u.financial_support)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1.5">
                      <IconBtn title={t("adm_approve")} onClick={() => update(u, { status: "approved" })} disabled={u.status === "approved"} testId={`admin-approve-${u.id}`}><Check className="h-4 w-4 text-emerald-500" /></IconBtn>
                      <IconBtn title={t("adm_reject")} onClick={() => update(u, { status: "rejected" })} disabled={u.status === "rejected"} testId={`admin-reject-${u.id}`}><X className="h-4 w-4 text-red-500" /></IconBtn>
                      <IconBtn title={t("adm_mark_paid")} onClick={() => update(u, { payment_status: "paid" })} disabled={u.payment_status === "paid"} testId={`admin-mark-paid-${u.id}`}><BadgeDollarSign className="h-4 w-4" style={{ color: "var(--gold)" }} /></IconBtn>
                      <IconBtn title={t("adm_edit")} onClick={() => setEditing(u)} testId={`admin-edit-${u.id}`}><Pencil className="h-4 w-4" /></IconBtn>
                      <IconBtn title={ready ? t("adm_idcard") : t("adm_id_not_ready")} onClick={() => setCard(u)} disabled={!ready} testId={`admin-idcard-${u.id}`}><IdIcon className="h-4 w-4" /></IconBtn>
                      <IconBtn title={u.status === "blocked" ? t("adm_unblock") : t("adm_block")} onClick={() => update(u, { status: u.status === "blocked" ? "approved" : "blocked" })} testId={`admin-block-${u.id}`}><Ban className={`h-4 w-4 ${u.status === "blocked" ? "text-emerald-500" : "text-zinc-400"}`} /></IconBtn>
                      <IconBtn title={t("adm_delete")} onClick={() => setDeleting(u)} testId={`admin-delete-${u.id}`}><Trash2 className="h-4 w-4 text-red-500" /></IconBtn>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {editing && <EditMemberDialog member={editing} onClose={() => setEditing(null)} onSave={async (body) => { await update(editing, body); setEditing(null); }} />}
      {card && <IdCardDialog member={card} onClose={() => setCard(null)} />}
      {adding && <AddMemberDialog onClose={() => setAdding(false)} onCreated={() => { setAdding(false); load(); onChange(); }} />}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-member-dialog">
          <AlertDialogHeader><AlertDialogTitle>{t("adm_delete")}: {deleting?.full_name}</AlertDialogTitle><AlertDialogDescription>{t("adm_delete_confirm")}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">{t("adm_cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 text-white hover:bg-red-700" data-testid="delete-confirm-btn">{t("adm_delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
